"use client";

import * as React from "react";
import Image from "next/image";
import {
    LazyMotion,
    domAnimation,
    m,
    useMotionValueEvent,
    useScroll,
    useTransform,
} from "framer-motion";

import Breadcrumbs, { type BreadcrumbItem } from "@/components/Breadcrumbs";
import Underline from "@/components/ui/underline";

export type TeamCard = {
    src: string;
    alt: string;
    name: string;
    bio: string;
    blur?: string;
};

export type AboutPageClientProps = {
    heading: string;
    description: string;
    cards: TeamCard[];
    breadcrumbs?: BreadcrumbItem[];
};

const slideUp = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

const CARD_SIZES =
    "(max-width: 767px) 100vw, (max-width: 1023px) 208px, (max-width: 1279px) 256px, (max-width: 1535px) 320px, 352px";

function shouldUnoptimize(src: string): boolean {
    return src.startsWith("http") && !src.includes("cdn.sanity.io");
}

type FlipCardProps = {
    item: TeamCard;
    index: number;
    flipped: boolean;
    onToggle: (index: number) => void;
    isInteractive?: boolean;
    x?: any;
    y?: any;
    tilt?: any;
    scale?: any;
    isMobileMode?: boolean;
};

const FlipCard = React.memo(function FlipCard({
                                                  item,
                                                  index,
                                                  flipped,
                                                  onToggle,
                                                  isInteractive = true,
                                                  x,
                                                  y,
                                                  tilt,
                                                  scale,
                                                  isMobileMode = false,
                                              }: FlipCardProps) {
    const cardNumber = String(index + 1).padStart(2, "0");
    const unoptimized = shouldUnoptimize(item.src);
    const isHero = index === 0;

    const MotionWrapper = isMobileMode ? "div" : m.div;

    const wrapperProps = isMobileMode
        ? { className: "w-full max-w-sm mx-auto relative" }
        : {
            style: { x, y, rotate: tilt, scale, willChange: "transform" },
            className: "absolute w-[9rem] sm:w-[13rem] lg:w-[16rem] xl:w-[20rem] 2xl:w-[22rem]",
        };

    return (
        // @ts-ignore
        <MotionWrapper {...wrapperProps}>
            <m.button
                type="button"
                onClick={() => onToggle(index)}
                disabled={!isInteractive}
                whileTap={{ scale: 0.98 }}
                className={`group block w-full transition-transform duration-300 ${
                    isInteractive
                        ? "cursor-pointer [@media(hover:hover)_and_(pointer:fine)]:hover:scale-[1.02]"
                        : "pointer-events-none cursor-default"
                } focus-visible:outline-none`}
                aria-label={`Otwórz kartę ${cardNumber}`}
            >
                <div className="relative aspect-[3/4] w-full" style={{ perspective: 1200 }}>
                    <m.div
                        className="relative h-full w-full"
                        style={{
                            transformStyle: "preserve-3d",
                            WebkitTransformStyle: "preserve-3d",
                            transform: "translateZ(0)",
                            willChange: "transform",
                        }}
                        animate={{ rotateY: flipped ? 180 : 0 }}
                        transition={{ type: "spring", stiffness: 220, damping: 22 }}
                    >
                        {/* Front */}
                        <div
                            className="absolute inset-0 overflow-hidden rounded-3xl bg-secondary-light/10"
                            style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
                        >
                            <Image
                                src={item.src}
                                alt={item.alt}
                                fill
                                sizes={CARD_SIZES}
                                loading={isHero ? "eager" : "lazy"}
                                fetchPriority={isHero ? "high" : "auto"}
                                priority={isHero}
                                decoding="async"
                                draggable={false}
                                placeholder={item.blur ? "blur" : "empty"}
                                blurDataURL={item.blur}
                                unoptimized={unoptimized}
                                className="object-cover rounded-3xl border-8 border-secondary transition duration-700 [@media(hover:hover)_and_(pointer:fine)]:group-hover:border-accent"
                            />
                            <div className="pointer-events-none absolute inset-x-0 bottom-0">
                                <div className="px-6 pb-6 pt-10 text-left">
                                    <p className="font-semibold uppercase text-white drop-shadow">kliknij</p>
                                </div>
                            </div>
                        </div>

                        {/* Back */}
                        <div
                            className="absolute inset-0 overflow-hidden rounded-3xl border-8 border-primary-light bg-secondary text-primary"
                            style={{
                                backfaceVisibility: "hidden",
                                WebkitBackfaceVisibility: "hidden",
                                transform: "rotateY(180deg) translateZ(0)",
                                WebkitTransform: "rotateY(180deg) translateZ(0)",
                            }}
                        >
                            {/* POPRAWKA TOTALNA: */}
                            {/* Mobile: p-6 | Tablet: !p-3 (wymuszone małe) | Desktop: !p-6 (wymuszony powrót do dużego) */}
                            <div className="flex h-full flex-col p-6 md:!p-3 lg:!p-6 text-left">

                                {/* Gap: Mobile 6 | Tablet 1 | Desktop 6 */}
                                <div className="flex items-start justify-between gap-6 md:!gap-1 lg:!gap-6">

                                    {/* Imię: Mobile xl/2xl | Tablet base (małe) | Desktop 2xl (duże) */}
                                    <h3 className="text-2xl md:!text-base lg:!text-2xl font-medium uppercase leading-tight">
                                        {item.name.split("\n").map((p, i) => (
                                            <React.Fragment key={i}>
                                                {p}
                                                <br />
                                            </React.Fragment>
                                        ))}
                                    </h3>

                                    {/* Numer: Mobile sm | Tablet 10px | Desktop sm */}
                                    <p className="text-sm md:!text-[10px] lg:!text-sm font-medium opacity-90">{cardNumber}</p>
                                </div>

                                <div className="mt-4 h-px w-full bg-white/20" />

                                {/* Bio: Mobile sm | Tablet 11px (bardzo małe) | Desktop sm */}
                                <p className="mt-auto text-sm md:!text-[11px] md:!leading-[1.2] lg:!text-sm lg:!leading-relaxed text-white/90">
                                    {item.bio}
                                </p>
                            </div>
                        </div>
                    </m.div>
                </div>
            </m.button>
        </MotionWrapper>
    );
});

export default function AboutPageClient({ heading, description, cards, breadcrumbs }: AboutPageClientProps) {
    const sectionRef = React.useRef<HTMLDivElement | null>(null);

    const [flippedIndex, setFlippedIndex] = React.useState<number | null>(null);
    const flippedIndexRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        flippedIndexRef.current = flippedIndex;
    }, [flippedIndex]);

    const [isInteractive, setIsInteractive] = React.useState(false);
    const isInteractiveRef = React.useRef(false);

    React.useEffect(() => {
        isInteractiveRef.current = isInteractive;
    }, [isInteractive]);

    const toggleFlip = React.useCallback((idx: number) => {
        setFlippedIndex((prev) => (prev === idx ? null : idx));
    }, []);

    const [isDesktop, setIsDesktop] = React.useState(false);
    React.useEffect(() => {
        const mq = window.matchMedia("(min-width: 768px)");
        const update = () => setIsDesktop(mq.matches);
        update();
        mq.addEventListener?.("change", update);
        return () => mq.removeEventListener?.("change", update);
    }, []);

    React.useEffect(() => {
        if (!isDesktop) setIsInteractive(true);
    }, [isDesktop]);

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
    const p = useTransform(scrollYProgress, [0, 0.22], [0, 1]);

    useMotionValueEvent(p, "change", (latest) => {
        const done = isDesktop ? latest >= 0.98 : true;
        if (done !== isInteractiveRef.current) setIsInteractive(done);
        if (isDesktop && !done && flippedIndexRef.current !== null) setFlippedIndex(null);
    });

    const textY = useTransform(p, [0, 1], ["0vh", "-26vh"]);
    const textOpacityScroll = useTransform(p, [0.65, 1], [1, 0]);

    const leftX = useTransform(p, [0, 1], ["-32vw", "-30vw"]);
    const leftY = useTransform(p, [0, 1], ["40vh", "28vh"]);
    const leftR = useTransform(p, [0, 1], ["-14deg", "0deg"]);
    const leftS = useTransform(p, [0, 1], [0.95, 1.18]);

    const centerX = useTransform(p, [0, 1], ["0vw", "0vw"]);
    const centerY = useTransform(p, [0, 1], ["52vh", "28vh"]);
    const centerR = useTransform(p, [0, 1], ["0deg", "0deg"]);
    const centerS = useTransform(p, [0, 1], [0.95, 1.18]);

    const rightX = useTransform(p, [0, 1], ["32vw", "30vw"]);
    const rightY = useTransform(p, [0, 1], ["40vh", "28vh"]);
    const rightR = useTransform(p, [0, 1], ["14deg", "0deg"]);
    const rightS = useTransform(p, [0, 1], [0.95, 1.18]);

    const headerContent = (
        <>
            {breadcrumbs && breadcrumbs.length > 0 && (
                <m.div
                    variants={slideUp}
                    custom={0}
                    className="relative inline-block will-change-transform mb-12 md:mb-16"
                >
                    <Breadcrumbs items={breadcrumbs} />
                    <Underline
                        delay={0.6}
                        useWhileInView
                        className="pointer-events-none absolute -left-4 top-full mt-2 text-accent h-5 w-[120px] md:h-6 md:w-[160px]"
                    />
                </m.div>
            )}
            <m.h1
                variants={slideUp}
                custom={breadcrumbs?.length ? 1 : 0}
                className="mb-5 text-3xl md:text-6xl font-medium md:mb-6 md:text-7xl leading-[1.1]"
            >
                {heading}
            </m.h1>
            <m.p
                variants={slideUp}
                custom={breadcrumbs?.length ? 2 : 1}
                className="text-base md:text-md max-w-md mx-auto"
            >
                {description}
            </m.p>
        </>
    );

    return (
        <LazyMotion features={domAnimation} strict>
            <section id="relume" className="mt-12 md:mt-16 lg:mt-28">
                <m.div
                    ref={sectionRef}
                    className="relative h-auto md:h-[280vh]"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                >
                    <div className="relative md:sticky md:top-0 min-h-screen md:h-screen overflow-hidden flex flex-col md:block">
                        <div className="px-[5%] pt-12 pb-16 md:pt-12 md:pb-24 lg:pt-14 lg:pb-28 flex-shrink-0">
                            <div className="container max-w-lg mx-auto">
                                <m.div
                                    style={isDesktop ? { y: textY, opacity: textOpacityScroll } : undefined}
                                    className="relative z-20 text-center will-change-transform"
                                >
                                    {headerContent}
                                </m.div>
                            </div>
                        </div>

                        {/* Mobile cards */}
                        <div className="block md:hidden mt-10 px-[5%] flex-1 pb-16">
                            <div className="flex flex-col gap-8">
                                {cards.map((card, i) => (
                                    <FlipCard
                                        key={`mobile-${i}`}
                                        item={card}
                                        index={i}
                                        flipped={flippedIndex === i}
                                        onToggle={toggleFlip}
                                        isMobileMode
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Desktop cards overlay */}
                        <div className="hidden md:flex absolute inset-0 z-10 items-start justify-center">
                            <FlipCard item={cards[0]} index={0} isInteractive={isInteractive} flipped={flippedIndex === 0} onToggle={toggleFlip} x={leftX} y={leftY} tilt={leftR} scale={leftS} />
                            <FlipCard item={cards[1]} index={1} isInteractive={isInteractive} flipped={flippedIndex === 1} onToggle={toggleFlip} x={centerX} y={centerY} tilt={centerR} scale={centerS} />
                            <FlipCard item={cards[2]} index={2} isInteractive={isInteractive} flipped={flippedIndex === 2} onToggle={toggleFlip} x={rightX} y={rightY} tilt={rightR} scale={rightS} />
                        </div>
                    </div>

                    <div className="absolute inset-0 -z-10 mt-[100vh] hidden md:block" />
                </m.div>
            </section>
        </LazyMotion>
    );
}